import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Eye, Package, Download, ExternalLink, Archive, Loader2, MessageCircle, Search, X } from "lucide-react";

const STATUSES = [
  { v: "new", label: "New", bg: "bg-yellow-200" },
  { v: "in_progress", label: "In progress", bg: "bg-blue-200" },
  { v: "completed", label: "Completed", bg: "bg-green-200" },
  { v: "cancelled", label: "Cancelled", bg: "bg-red-200" },
];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/orders/${id}`, { status });
    toast.success("Status updated");
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const exportJson = (o) => {
    const blob = new Blob([JSON.stringify(o, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `order-${o.id}.json`;
    a.click();
  };

  const downloadZip = async (o) => {
    setZipping(true);
    try {
      const res = await api.get(`/orders/${o.id}/download-zip`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `order-${o.id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success("ZIP downloaded");
    } catch {
      toast.error("ZIP download failed");
    }
    setZipping(false);
  };

  const [exportingCsv, setExportingCsv] = useState(false);
  const exportAllOrdersCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await api.get("/orders/export.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "orders_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success("CSV exported");
    } catch {
      toast.error("CSV export failed");
    }
    setExportingCsv(false);
  };

  // One-tap "Send preview to WhatsApp" — opens WhatsApp click-to-chat with a pre-filled message
  // pointing at the buyer's design preview. Requires no API key (wa.me deep link). The buyer's
  // number is preferred; falls back to the phone field if WhatsApp isn't captured separately.
  const sendPreviewWhatsApp = (o) => {
    const raw = (o.customer_whatsapp || o.customer_phone || "").trim();
    const digits = raw.replace(/[^\d]/g, "");
    if (!digits) { toast.error("No WhatsApp number on this order"); return; }
    const previewAbs = o.preview_url
      ? new URL(fileUrl(o.preview_url), window.location.origin).toString()
      : "";
    const idShort = o.custom_order_id || (o.id || "").slice(0, 8);
    const lines = [
      `Hi ${o.customer_name || "there"}!`,
      `Here's the preview of your custom "${o.template_name || "order"}":`,
    ];
    if (previewAbs) lines.push(previewAbs);
    lines.push(`Order ref: ${idShort}`);
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Quick jump-to-order search — matches the human-friendly custom_order_id (aa00000001 style,
  // with or without the "aa" prefix) first, then falls back to customer name/email as a bonus.
  const filteredOrders = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) =>
      (o.custom_order_id || "").toLowerCase().includes(q) ||
      (o.customer_name || "").toLowerCase().includes(q) ||
      (o.customer_email || "").toLowerCase().includes(q)
    );
  })();

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-600">Incoming</div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl">Customer Orders</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by order # (e.g. aa00000007)"
                className="brutal-input pl-9 pr-8 py-2 text-sm rounded-md w-64"
                data-testid="order-search-input"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-900"
                  data-testid="order-search-clear-btn"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={exportAllOrdersCsv}
              disabled={exportingCsv}
              className="brutal-btn bg-white px-4 py-2 rounded-md text-sm flex items-center gap-1 disabled:opacity-50"
              data-testid="export-all-orders-csv-btn"
            >
              {exportingCsv ? <><Loader2 className="w-4 h-4 animate-spin"/> Exporting…</> : <><Download className="w-4 h-4"/> Export all orders (CSV)</>}
            </button>
          </div>
        </div>

        {loading ? <div className="brutal-card p-6">Loading…</div> :
         orders.length === 0 ? (
          <div className="brutal-card p-10 text-center">
            <Package className="w-10 h-10 mx-auto mb-3 text-stone-500" />
            <div className="font-display text-3xl">No orders yet</div>
            <p className="text-stone-600 mt-1">Share a template link and orders will land here.</p>
          </div>
         ) : filteredOrders.length === 0 ? (
          <div className="brutal-card p-10 text-center" data-testid="order-search-no-results">
            <Search className="w-10 h-10 mx-auto mb-3 text-stone-500" />
            <div className="font-display text-3xl">No matching orders</div>
            <p className="text-stone-600 mt-1">No order matches &ldquo;{search}&rdquo;. Try the order reference (aa########), name, or email.</p>
          </div>
         ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {filteredOrders.map((o) => (
                <div key={o.id} onClick={() => setSelected(o)} className={`brutal-card p-4 flex gap-4 cursor-pointer ${selected?.id === o.id ? 'bg-yellow-50' : ''}`} data-testid={`order-row-${o.id}`}>
                  <div className="w-20 h-20 bg-stone-100 border-2 border-stone-900 rounded-md overflow-hidden flex-shrink-0">
                    {o.preview_url ? <img src={fileUrl(o.preview_url)} alt="preview" className="w-full h-full object-cover"/> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-heading font-bold text-lg truncate">{o.customer_name}</div>
                      <span className={`text-xs font-bold uppercase border-2 border-stone-900 rounded px-2 py-0.5 ${STATUSES.find(s=>s.v===o.status)?.bg || 'bg-stone-100'}`}>{o.status}</span>
                    </div>
                    {o.custom_order_id && (
                      <div className="text-xs font-mono font-bold text-stone-500" data-testid={`order-custom-id-${o.id}`}>#{o.custom_order_id}</div>
                    )}
                    <div className="text-sm text-stone-700 truncate">{o.customer_email} · {o.template_name}</div>
                    <div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>

            <div>
              {selected ? (
                <div className="brutal-card p-5 sticky top-24" data-testid="order-detail">
                  {selected.preview_url && (
                    <div className="aspect-square bg-stone-100 border-2 border-stone-900 rounded-md overflow-hidden mb-4">
                      <img src={fileUrl(selected.preview_url)} alt="preview" className="w-full h-full object-contain"/>
                    </div>
                  )}
                  <div className="font-heading font-black text-2xl">{selected.customer_name}</div>
                  {selected.custom_order_id && (
                    <div className="text-sm font-mono font-bold text-fuchsia-700" data-testid="order-detail-custom-id">Order #{selected.custom_order_id}</div>
                  )}
                  <div className="text-sm text-stone-700">{selected.customer_email}</div>
                  {selected.customer_phone && <div className="text-sm text-stone-700">📞 {selected.customer_phone}</div>}
                  {selected.customer_whatsapp && (
                    <a href={`https://wa.me/${selected.customer_whatsapp.replace(/[^\d+]/g, "")}`} target="_blank" rel="noreferrer" className="text-sm text-green-800 hover:underline block mt-1" data-testid="order-whatsapp">
                      📱 WhatsApp: {selected.customer_whatsapp} (tap to open chat)
                    </a>
                  )}
                  <div className="mt-3 text-sm"><span className="font-bold">Template:</span> {selected.template_name}</div>
                  {selected.notes && <div className="mt-2 text-sm"><span className="font-bold">Notes:</span> {selected.notes}</div>}

                  {(selected.shopify_checkout_url || selected.shopify_error) && (
                    <div className="mt-3 border-t-2 border-stone-900 pt-3">
                      <div className="text-xs font-bold uppercase tracking-widest mb-2">Shopify checkout</div>
                      {selected.shopify_checkout_url ? (
                        <a href={selected.shopify_checkout_url} target="_blank" rel="noreferrer" className="text-sm text-green-800 hover:underline inline-flex items-center gap-1" data-testid="order-shopify-checkout-link">
                          <ExternalLink className="w-3 h-3"/> Checkout was created — view on Shopify
                        </a>
                      ) : (
                        <div className="text-xs bg-red-50 border-2 border-red-300 rounded p-2 text-red-800" data-testid="order-shopify-error">
                          <span className="font-bold">{selected.shopify_error.code}:</span> {selected.shopify_error.message}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-4 border-t-2 border-stone-900 pt-3">
                    <div className="text-xs font-bold uppercase tracking-widest mb-2">Design details</div>
                    <div className="max-h-56 overflow-auto bg-stone-100 border-2 border-stone-900 rounded p-2 text-xs font-mono">
                      {Object.entries(selected.design_data || {}).map(([zid, d]) => (
                        <div key={zid} className="mb-2">
                          <div className="font-bold">Zone {zid.slice(0,6)} ({d.type})</div>
                          {d.type === "text" && (
                            <div>text: &ldquo;{d.text}&rdquo; &middot; font: {d.font} &middot; color: {d.color}</div>
                          )}
                          {d.type === "image" && (
                            <div>image: <a className="underline" target="_blank" rel="noreferrer" href={fileUrl(d.image_url)}>view</a></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {STATUSES.map((s) => (
                      <button key={s.v} onClick={() => setStatus(selected.id, s.v)} className={`brutal-btn ${s.bg} px-3 py-2 rounded-md text-xs`} data-testid={`set-status-${s.v}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => exportJson(selected)} className="brutal-btn bg-white px-3 py-2 rounded-md text-sm flex items-center gap-1 flex-1 min-w-[120px]" data-testid="export-order">
                      <Download className="w-4 h-4"/> Export JSON
                    </button>
                    {selected.preview_url && (
                      <a
                        href={fileUrl(selected.preview_url)}
                        download={`order-${selected.id}-hires.png`}
                        target="_blank" rel="noreferrer"
                        className="brutal-btn bg-yellow-300 px-3 py-2 rounded-md text-sm flex items-center gap-1 flex-1 min-w-[120px]"
                        data-testid="download-hires-png"
                      >
                        <Download className="w-4 h-4"/> Hi-res PNG
                      </a>
                    )}
                    {selected.preview_svg_urls?.[0] && (
                      <a
                        href={fileUrl(selected.preview_svg_urls[0])}
                        download={`order-${selected.id}-hires.svg`}
                        target="_blank" rel="noreferrer"
                        className="brutal-btn bg-orange-300 px-3 py-2 rounded-md text-sm flex items-center gap-1 flex-1 min-w-[120px]"
                        data-testid="download-svg-btn"
                      >
                        <Download className="w-4 h-4"/> Download SVG
                      </a>
                    )}
                    <button
                      onClick={() => downloadZip(selected)}
                      disabled={zipping}
                      className="brutal-btn bg-fuchsia-300 px-3 py-2 rounded-md text-sm flex items-center gap-1 flex-1 min-w-[140px] disabled:opacity-50"
                      data-testid="download-zip-btn"
                    >
                      {zipping ? <><Loader2 className="w-4 h-4 animate-spin"/> Packaging…</> : <><Archive className="w-4 h-4"/> Download all (ZIP)</>}
                    </button>
                    {(selected.customer_whatsapp || selected.customer_phone) && selected.preview_url && (
                      <button
                        onClick={() => sendPreviewWhatsApp(selected)}
                        className="brutal-btn bg-green-300 px-3 py-2 rounded-md text-sm flex items-center gap-1 flex-1 min-w-[160px]"
                        data-testid="send-preview-whatsapp"
                        title={`Opens WhatsApp with the preview pre-filled for ${selected.customer_whatsapp || selected.customer_phone}`}
                      >
                        <MessageCircle className="w-4 h-4"/> Send preview to WhatsApp
                      </button>
                    )}
                  </div>
                  {selected.preview_url && (
                    <a href={fileUrl(selected.preview_url)} target="_blank" rel="noreferrer" className="text-xs text-stone-500 hover:underline mt-2 inline-flex items-center gap-1" data-testid="open-png-newtab">
                      <ExternalLink className="w-3 h-3"/> Open PNG in new tab
                    </a>
                  )}
                </div>
              ) : (
                <div className="brutal-card p-6 text-center text-stone-600">
                  <Eye className="w-8 h-8 mx-auto mb-2" />
                  Select an order to see details.
                </div>
              )}
            </div>
          </div>
         )}
      </div>
    </div>
  );
}
